import useDialog from 'hooks/common/useDialog';
import { useState, useEffect, useMemo, useCallback } from 'react';
import useIntersectionObserver from 'hooks/common/useIntersectionObserver';

const INIT_NUM_LOAD = 10;
const NUM_LOAD = 25;

const useBlock = () => {
  const { open: openDialog } = useDialog();
  const [isLoading, setIsLoading] = useState(false);
  const [blockSearchQuery, setBlockSearchQuery] = useState('');
  const [hiddenSearchQuery, setHiddenSearchQuery] = useState('');
  const [blockUidList, setBlockUidList] = useState<string[]>([]);
  const [hiddenUidList, setHiddenUidList] = useState<string[]>([]);
  const blockHasMore = blockUidList.length > 0;
  const hiddenHasMore = hiddenUidList.length > 0;
  const [blockUserInfoList, setBlockUserInfoList] = useState<UserInfo[]>([]);
  const [hiddenUserInfoList, setHiddenUserInfoList] = useState<UserInfo[]>([]);

  // 초기화
  useEffect(() => {
    const init = async () => {
      setIsLoading(true);
      await FriendsDataManager.startPageAfterInitializeAsync();

      const [fetchedBlockUidList, fetchedHiddenUidList] = await Promise.all([
        new Promise<string[]>((resolve) => {
          FriendsDataManager.getBlockList((list: { uid: string }[]) => {
            resolve(list.map((item) => item.uid));
          });
        }),
        new Promise<string[]>((resolve) => {
          FriendsDataManager.getHiddenList((list: { uid: string }[]) => {
            resolve(list.map((item) => item.uid));
          });
        }),
      ]);

      // for test
      // if (fetchedBlockUidList.length > 0) {
      //   const testBlockUidList = Array(100).fill(fetchedBlockUidList[0]);
      //   const fetchedBlockUserInfoList = await FriendsDataManager.getUserInfoList(testBlockUidList.slice(0, INIT_NUM_LOAD));
      //   setBlockUserInfoList(fetchedBlockUserInfoList);
      //   setBlockUidList(testBlockUidList.slice(INIT_NUM_LOAD));
      // }
      // if (fetchedHiddenUidList.length > 0) {
      //   const testHiddenUidList = Array(100).fill(fetchedHiddenUidList[0]);
      //   const fetchedHiddenUserInfoList = await FriendsDataManager.getUserInfoList(testHiddenUidList.slice(0, INIT_NUM_LOAD));
      //   setHiddenUserInfoList(fetchedHiddenUserInfoList);
      //   setHiddenUidList(testHiddenUidList.slice(INIT_NUM_LOAD));
      // }

      if (fetchedBlockUidList.length > 0) {
        const fetchedBlockUserInfoList = await FriendsDataManager.getUserInfoList(fetchedBlockUidList.slice(0, INIT_NUM_LOAD));
        setBlockUserInfoList(fetchedBlockUserInfoList);
        setBlockUidList(fetchedBlockUidList.slice(INIT_NUM_LOAD));
      }
      if (fetchedHiddenUidList.length > 0) {
        const fetchedHiddenUserInfoList = await FriendsDataManager.getUserInfoList(fetchedHiddenUidList.slice(0, INIT_NUM_LOAD));
        setHiddenUserInfoList(fetchedHiddenUserInfoList);
        setHiddenUidList(fetchedHiddenUidList.slice(INIT_NUM_LOAD));
      }

      setIsLoading(false);
    };

    init();

    return () => {
      setIsLoading(false);
      FriendsDataManager.stopPageAfterInitialize(() => {});
    };
  }, []);

  // 유저 정보 로드
  const loadBlockUserInfoList = useCallback(async () => {
    if (blockUidList.length === 0) return;
    setIsLoading(true);
    const fetchedBlockUserInfoList = await FriendsDataManager.getUserInfoList(blockUidList.slice(0, NUM_LOAD));
    setBlockUidList((prev) => prev.slice(NUM_LOAD));
    setBlockUserInfoList((prev) => [...prev, ...fetchedBlockUserInfoList]);
    setIsLoading(false);
  }, [blockUidList]);

  const loadHiddenUserInfoList = useCallback(async () => {
    if (hiddenUidList.length === 0) return;
    setIsLoading(true);
    const fetchedHiddenUserInfoList = await FriendsDataManager.getUserInfoList(hiddenUidList.slice(0, NUM_LOAD));
    setHiddenUidList((prev) => prev.slice(NUM_LOAD));
    setHiddenUserInfoList((prev) => [...prev, ...fetchedHiddenUserInfoList]);
    setIsLoading(false);
  }, [hiddenUidList]);

  const blockObserverRef = useIntersectionObserver({ onIntersect: loadBlockUserInfoList, enabled: blockHasMore && !isLoading });
  const hiddenObserverRef = useIntersectionObserver({ onIntersect: loadHiddenUserInfoList, enabled: hiddenHasMore && !isLoading });

  // 검색 필터
  const filteredBlockUserInfoList = useMemo(
    () => blockUserInfoList.filter((userInfo) => userInfo.nick.toLowerCase().startsWith(blockSearchQuery.toLowerCase())),
    [blockUserInfoList, blockSearchQuery]
  );
  const filteredHiddenUserInfoList = useMemo(
    () => hiddenUserInfoList.filter((userInfo) => userInfo.nick.toLowerCase().startsWith(hiddenSearchQuery.toLowerCase())),
    [hiddenUserInfoList, hiddenSearchQuery]
  );

  // unblock
  const handleUnblockButtonClick = useCallback((userInfo: UserInfo) => {
    const msg = `<span>${brsc.ask_unblock.replace('"$param"', `<b>@${userInfo.nick}</b>`)}</span>`;
    openDialog({ description: msg, cancelText: brsc.no, confirmText: brsc.yes }).then((result: boolean) => {
      if (result) {
        FriendsDataManager.setBlock(userInfo.fbUid, false);
        setBlockUserInfoList((prev) => prev.filter((info: UserInfo) => info.fbUid !== userInfo.fbUid));
      }
    });
  }, [openDialog]);

  // unhide
  const handleUnhideButtonClick = useCallback((userInfo: UserInfo) => {
    FriendsDataManager.revealFollower(userInfo.fbUid);
    setHiddenUserInfoList((prev) => prev.filter((info: UserInfo) => info.fbUid !== userInfo.fbUid));
  }, []);

  return {
    isLoading,
    blockSearchQuery,
    setBlockSearchQuery,
    hiddenSearchQuery,
    setHiddenSearchQuery,
    blockObserverRef,
    hiddenObserverRef,
    blockHasMore,
    hiddenHasMore,
    filteredBlockUserInfoList,
    filteredHiddenUserInfoList,
    handleUnblockButtonClick,
    handleUnhideButtonClick,
  };
};

export default useBlock;
